// src/services/postImageUpload.js — get a picture from Danielle's device into
// a Coach Post (admin only). Shrinks it in the browser first so members load a
// light file, then sends the finished JPEG to /api/upload-post-image.

import { fetchImagesFromFolder } from './bunnyStream';

// Longest side after shrinking. 2000px stays crisp full-screen in the
// tap-to-enlarge view on a retina phone, while a typical screenshot lands
// around 200–500 KB instead of several MB.
const MAX_SIDE = 2000;
const MAX_INPUT_BYTES = 40 * 1024 * 1024; // refuse absurd files before decoding

// Decode via an <img>: it applies the photo's EXIF orientation, so a sideways
// phone photo comes out upright.
function decode(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => resolve({ img, url });
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
        img.src = url;
    });
}

// Returns { blob, width, height, previewUrl } or throws Error(code) where code
// is 'not-image' | 'too-large' | 'decode' | 'encode'.
export async function prepareImage(file) {
    if (!file) throw new Error('not-image');
    if (file.type && !file.type.startsWith('image/')) throw new Error('not-image');
    if (file.size > MAX_INPUT_BYTES) throw new Error('too-large');
    const { img, url } = await decode(file);
    try {
        const w0 = img.naturalWidth, h0 = img.naturalHeight;
        if (!w0 || !h0) throw new Error('decode');
        const scale = Math.min(1, MAX_SIDE / Math.max(w0, h0));
        const w = Math.max(1, Math.round(w0 * scale)), h = Math.max(1, Math.round(h0 * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        // White under any transparency (PNG screenshots), since JPEG has no alpha.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, w, h);
        const blob = await new Promise((resolve, reject) => {
            canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode'))), 'image/jpeg', 0.86);
        });
        return { blob, width: w, height: h, previewUrl: URL.createObjectURL(blob) };
    } finally {
        URL.revokeObjectURL(url);
    }
}

// Returns { url } on success, or { error } with
// 'not-configured' | 'too-large' | 'not-an-image' | 'storage-failed' | 'network'.
export async function uploadPostImage(blob, getToken) {
    try {
        const token = await getToken();
        const r = await fetch('/api/upload-post-image', {
            method: 'POST',
            headers: { 'Content-Type': 'image/jpeg', Authorization: `Bearer ${token}` },
            body: blob,
        });
        const data = await r.json().catch(() => ({}));
        if (r.ok && data.url) return { url: data.url };
        return { error: data.error || 'storage-failed' };
    } catch (e) {
        return { error: 'network' };
    }
}

// The library: Danielle's uploaded post images first (newest first), then the
// existing program images. Either folder failing just leaves it out.
export async function fetchPostImageLibrary() {
    const [posts, programs] = await Promise.all([
        fetchImagesFromFolder('post-images').catch(() => null),
        fetchImagesFromFolder('Program Images').catch(() => null),
    ]);
    const byNewest = (a, b) => String(b.createdTime || '').localeCompare(String(a.createdTime || ''));
    return [...(posts || []).sort(byNewest), ...(programs || [])];
}
